/* IS ANYTHING BROKEN RIGHT NOW?
 *
 * 127 catch blocks report well individually and nothing answers that question at
 * a glance. The Sync health page is passive: it answers only when the DM presses
 * four Check buttons, which is to say it answers after they already suspect
 * something.
 *
 * NO NEW POLLING. The obvious build -- probe each system on a timer -- is the
 * wrong one twice over: there are already seven polling intervals (S5-5 is about
 * reducing them), and a synthetic probe reports on the probe rather than on the
 * work. Instead this listens to the traffic that ALREADY happens. Every bridge
 * request resolves ok, resolves with an error, or times out, and that is a
 * truthful signal about the system it was aimed at: "the last thing we actually
 * asked Roll20 to do failed" is better information than "a ping succeeded".
 *
 * The cost is that a system nobody has used yet is UNKNOWN rather than green,
 * which is correct and worth saying out loud: this reports observed health, not
 * reachability. A green lamp means something real worked recently.
 */
export type Status = "unknown" | "ok" | "flapping" | "down";
export type System = "connector" | "roll20" | "ddb" | "compass";

export interface SystemHealth {
  system: System;
  status: Status;
  /** when something last worked */
  okAt: number;
  /** the last failure and when, kept even once things recover */
  error: string;
  errorAt: number;
  attempts: number;
  failures: number;
}
export type Health = Record<System, SystemHealth>;

export interface Outcome {
  /** the request type, e.g. DM_SCREEN_ROLL20_BARS_REQUEST */
  lane: string;
  ok: boolean;
  error?: string;
  at: number;
}

export const SYSTEM_LABEL: Record<System, string> = {
  connector: "Connector", roll20: "Roll20", ddb: "D&D Beyond", compass: "Compass",
};

const fresh = (system: System): SystemHealth =>
  ({ system, status: "unknown", okAt: 0, error: "", errorAt: 0, attempts: 0, failures: 0 });

export const emptyHealth = (): Health =>
  ({ connector: fresh("connector"), roll20: fresh("roll20"), ddb: fresh("ddb"), compass: fresh("compass") });

/* WHICH SYSTEM A LANE IS AIMED AT.
 *
 * Declared rather than derived. There is no reliable naming convention here --
 * the same lesson the capability gating in the contract test learned -- and a
 * lane filed under the wrong system makes a lamp lie, which is worse than no
 * lamp. Anything unrecognised is attributed to the connector, because reaching
 * the connector is the one thing every lane has in common. */
const PREFIXES: [RegExp, System][] = [
  [/^DM_SCREEN_ROLL20_/, "roll20"],
  [/^DM_SCREEN_LMC_/, "compass"],
  [/^DM_SCREEN_(DDB_|SHEET|CHARACTER|TAGGED_NOTES_|DM_NOTES_|MONSTER_CATALOG_|EQUIPMENT_|INVENTORY_|SPELLBOOK_|ITEM_|HOMEBREW_)/, "ddb"],
  [/^DM_SCREEN_BRIDGE_/, "connector"],
];
export function systemOf(lane: string): System {
  for (const [re, system] of PREFIXES) if (re.test(String(lane || ""))) return system;
  return "connector";
}

/** How long a recent failure keeps a recovered system amber. A DM who saw a
 *  write fail wants to know it was flaky even once the next one worked. */
export const FLAP_WINDOW_MS = 120_000;

/** A lane can answer `ok: false` and still be working perfectly: it created the item but the catalogue has not
 *  indexed it yet (pendingAdd), it refused a duplicate on purpose, or it said a request is unsupported. Those are
 *  answers, not faults, and counting them turned the global Connector lamp red for a normal "not yet" (seen live
 *  2026-10-08 after creating a magic item). Anything else with ok: false is still a failure. */
export function answeredNormally(m: { ok?: unknown; created?: unknown; duplicate?: unknown; pendingAdd?: unknown; unsupported?: unknown }): boolean {
  if (m.ok !== false) return true;
  return m.created === true || m.duplicate === true || m.pendingAdd === true || m.unsupported === true;
}

/** Fold one outcome in. Pure: the same inputs give the same health, which is why
 *  every rule below is testable without a browser or a connector. */
export function observe(health: Health, outcome: Outcome): Health {
  const system = systemOf(outcome.lane);
  const was = health[system];
  const next: SystemHealth = { ...was, attempts: was.attempts + 1 };
  if (outcome.ok) {
    next.okAt = outcome.at;
    /* Recovered, but recently broken: amber, not green. The failure and its time
       are KEPT -- a lamp that forgets why it went red is a lamp that cannot be
       asked about it. */
    next.status = was.errorAt && outcome.at - was.errorAt < FLAP_WINDOW_MS ? "flapping" : "ok";
  } else {
    next.failures = was.failures + 1;
    next.error = outcome.error || "Something failed and said nothing";
    next.errorAt = outcome.at;
    next.status = "down";
  }
  return { ...health, [system]: next };
}

/** The connector's own lamp comes from the handshake rather than from traffic:
 *  it is the one system whose absence is knowable without asking it anything. */
export function withConnector(health: Health, connected: boolean, version: string, lastSeenAt: number): Health {
  const was = health.connector;
  return {
    ...health,
    connector: connected
      ? { ...was, status: was.errorAt && Date.now() - was.errorAt < FLAP_WINDOW_MS ? "flapping" : "ok", okAt: lastSeenAt || Date.now() }
      : { ...was, status: "down", error: version ? `Connector ${version} stopped answering` : "No connector detected", errorAt: was.errorAt || Date.now() },
  };
}

/** The single answer to "is anything broken right now?" -- the worst lamp on the
 *  board, because that is the one the DM needs to know about. */
export function worst(health: Health): Status {
  const all = Object.values(health).map((h) => h.status);
  if (all.includes("down")) return "down";
  if (all.includes("flapping")) return "flapping";
  if (all.includes("ok")) return "ok";
  return "unknown";
}

/** Which systems are worth showing as a problem, worst first. */
export const troubled = (health: Health): SystemHealth[] =>
  Object.values(health)
    .filter((h) => h.status === "down" || h.status === "flapping")
    .sort((a, b) => (a.status === b.status ? b.errorAt - a.errorAt : a.status === "down" ? -1 : 1));

/** A phrase for a lamp's tooltip. Says WHEN, because "failed" without a time is
 *  unactionable -- a DM needs to know whether it was this turn or an hour ago. */
export function describe(h: SystemHealth, now = Date.now()): string {
  const ago = (t: number) => {
    const s = Math.max(0, Math.round((now - t) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.round(s / 60);
    return m < 60 ? `${m} min ago` : `${Math.round(m / 60)}h ago`;
  };
  switch (h.status) {
    case "unknown": return `${SYSTEM_LABEL[h.system]}: nothing has been asked of it yet this session.`;
    case "ok": return `${SYSTEM_LABEL[h.system]}: working, last succeeded ${ago(h.okAt)}.`;
    case "flapping": return `${SYSTEM_LABEL[h.system]}: working now, but failed ${ago(h.errorAt)} — ${h.error}`;
    case "down": return `${SYSTEM_LABEL[h.system]}: failing as of ${ago(h.errorAt)} — ${h.error}`;
  }
}
